/**
 * Agent key file loading. The key file lives at
 * `~/.config/atbash/guard-client-key` by default and is either JSON
 * (`{ privKey, pubKey }`) or `key=value` lines (`privkey=…`, `pubkey=…`).
 * Only the private key is needed — the pubkey is re-derived by the Rust core
 * via `loadAgent`.
 */
import { readFileSync } from "node:fs";
import { homedir } from "node:os";
import { join } from "node:path";

import { native } from "./native.js";
import type { AgentAuth } from "./types.js";

const DEFAULT_KEY_PATH_REL = ".config/atbash/guard-client-key";

export function resolveKeyPath(input?: string): string {
  if (input) return expandHome(input);
  const home = process.env.HOME || homedir() || "";
  return join(home, DEFAULT_KEY_PATH_REL);
}

function expandHome(p: string): string {
  if (!p.startsWith("~/")) return p;
  const home = process.env.HOME || homedir() || "";
  return join(home, p.slice(2));
}

function readKeyFile(keyPath: string): { privKey: string; pubKey: string } {
  const content = String(readFileSync(keyPath, "utf8") || "").trim();
  let privKey = "";
  let pubKey = "";

  if (content.startsWith("{")) {
    const creds = JSON.parse(content);
    privKey = String(
      creds.privKey || creds.privkey || creds.privateKey || "",
    ).trim();
    pubKey = String(
      creds.pubKey || creds.pubkey || creds.publicKey || "",
    ).trim();
  } else {
    for (const line of content.split(/\r?\n/)) {
      if (line.startsWith("privkey="))
        privKey = line.slice("privkey=".length).trim();
      if (line.startsWith("pubkey="))
        pubKey = line.slice("pubkey=".length).trim();
    }
  }

  if (!privKey || !pubKey) {
    throw new Error(`atbash key file missing priv/pub key fields: ${keyPath}`);
  }

  privKey = privKey.replace(/^0x/, "");
  return { privKey, pubKey };
}

export function loadAgentFromFile(keyPath?: string): AgentAuth {
  const resolved = resolveKeyPath(keyPath);
  const { privKey } = readKeyFile(resolved);
  return native.loadAgent(privKey);
}
